
import { defineComponent, ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const TeamRepository = RepositoryFactory.get('team')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog, { Dialog } from '@/components/BaseDialog.vue'

import TeamInformation from '@/components/teamDetail/TeamInformation.vue'
import TeamPlayer from '@/components/teamDetail/TeamPlayer.vue'

import { Team } from '@/types/team'
import { Player } from '@/types/player'

import { User } from '@/types/user'

interface UserForm extends User {
  password?: string

  hasPasswordChanged?: boolean
  isEditing?: boolean
}

type TabId = 'team' | 'player' | 'account'

export default defineComponent({
  name: 'TeamDetail',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
    BaseDialog,

    TeamInformation,
    TeamPlayer
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    onMounted(() => {
      getTeam()
    })

    const team: {
      data?: Team
      players: Player[]
      accounts: UserForm[]
    } = reactive({
      data: undefined,
      players: [],
      accounts: []
    })

    const getTeam = async () => {
      try {
        const { team_id } = route.params

        const res = await TeamRepository.getTeam(team_id)

        if (res.status === 200) {
          const r = res.data

          team.data = r.data.team
          team.players = r.data.players
          team.accounts = r.data.accounts
        }
      } catch (error) {
        //
      }
    }

    const tab: {
      id: TabId
    } = reactive({
      id: 'team'
    })

    const onClickTab = (event: Event) => {
      const element = event.target as HTMLInputElement
      const tabState = element.getAttribute('aria-controls') as TabId

      if (tabState!== 'account' && isAccountPanelEditing.value) {
        toPageData.tabPanelId = tabState
        openAccountDialogLeave()
        return
      }

      tab.id = tabState
    }

    const toPageData: {
      routeFullPath?: string,
      tabPanelId?: TabId
    } = reactive({
      routeFullPath: undefined,
      tabPanelId: undefined
    })

    const initializeToPageData = () => {
      toPageData.routeFullPath = undefined
      toPageData.tabPanelId = undefined
    }

    const isAccountPanelEditing = ref<boolean>(false)

    const changeStateAccountEditing = (isAccountEditing: boolean) => {
      isAccountPanelEditing.value = isAccountEditing
    }

    const canLeave = ref<boolean>(false)

    const accountDialogLeave = reactive<Dialog>({
      title: 'このページを離れますか？',
      message: '編集中のアカウントがあります。\n追加・更新をせずにページを離れると、編集中の内容は保存されません。',
      confirmText: 'ページを離れる',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openAccountDialogLeave = () => {
      accountDialogLeave.isOpen = true
    }

    const closeAccountDialogLeave = () => {
      accountDialogLeave.isOpen = false
      initializeToPageData()
    }

    onBeforeRouteLeave((to: any, from: any, next: any) => {
      if (isAccountPanelEditing.value) {
        toPageData.routeFullPath = to.fullPath
        openAccountDialogLeave()

        if (canLeave.value) {
          next()
        } else {
          next(false)
        }
      } else {
        next()
      }
    })

    const onConfirmAccountDialogLaeve = () => {
      if (toPageData.tabPanelId != undefined) {
        /* 同ページ内の別タブへ移動 */
        tab.id = toPageData.tabPanelId
        closeAccountDialogLeave()
      } else {
        /* 同サイト内の別ページへ移動 */
        leaveAccountPage()
      }
    }

    const leaveAccountPage = () => {
      canLeave.value = true

      if (toPageData.routeFullPath == undefined) return
      router.push({ path: toPageData.routeFullPath })
      openAccountDialogLeave()
    }

    /* 他サイトへの離脱、ブラウザ更新、ブラウザタブを閉じる時など */
    onMounted(() => {
      window.addEventListener('beforeunload', confirmLeavePage)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', confirmLeavePage)
    })

    const confirmLeavePage = (event: Event) => {
      if (tab.id !== 'account') return

      if (!canLeave.value && isAccountPanelEditing.value) {
        // Cancel the event as stated by the standard.
        event.preventDefault()
        // Chrome requires returnValue to be set.
        event.returnValue = true
      }
    }

    useHead({
      title: computed(() => team.data?.name + ' - チーム詳細')
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: 'チーム一覧',
            path: '/team'
          }
        },
        {
          type: 'text',
          text: {
            name: team.data?.name
          }
        }
      ] as Breadcrumb[]
    })

    return {
      team,
      getTeam,

      tab,
      onClickTab,

      toPageData,
      initializeToPageData,

      isAccountPanelEditing,
      changeStateAccountEditing,

      accountDialogLeave,
      openAccountDialogLeave,
      closeAccountDialogLeave,
      onConfirmAccountDialogLaeve,
      leaveAccountPage,

      breadcrumbs
    }
  }
})
